import { useSearchParams } from '@solidjs/router';
import { createSignal } from 'solid-js';
import { redirectToAuth } from '~/config/auth';
import { createMagicDoorContext } from '~/contexts/utils';
import { companyRepository } from '~/repositories/companiesRepository';
import { MeRepository } from '~/repositories/meRepository';
import { isDemo, LoginType } from '~/utils/constant';
import { getImageUrl } from '~/utils/file';
import { createLazyResource, createMutation } from '~/utils/resource';

export type Profile = {
  id: string;
  name: string;
  firstName: string;
  lastName?: string;
  jobTitle?: string;
  email: string;
  phone?: string;
  address?: string;
  memo?: string;
  avatar: string;
  keywords?: string;
  createdAt: string;
};

const meRepo = new MeRepository();

const dto2Profile = (dto: MagicDoor.Api.PropertyManagerDto): Profile => {
  return {
    name: dto.name ?? `${dto.firstName} ${dto.lastName ?? ''}`.trim(),
    firstName: dto.firstName,
    lastName: dto.lastName ?? '',
    email: dto.email,
    avatar: getImageUrl('avatar', dto.id, dto.imageId ?? ''),
    id: dto.id,
    createdAt: dto.created,
  };
};

export const [AuthProvider, useAuth] = createMagicDoorContext('Auth', () => {
  const [loginType, setLoginType] = createSignal<LoginType>(LoginType.Password);

  const [me, { fetch, refetch, mutate }] = createLazyResource<Profile>(async () => {
    const data = await meRepo.getProfile();
    return dto2Profile(data);
  });

  const updateProfile = createMutation(async (profile: Profile) => {
    const result = await meRepo.updateProfile(profile);
    mutate(dto2Profile(result));
  });

  const updateAvatar = createMutation(async (avatar: File) => {
    const result = await meRepo.updateProfileImage(avatar);
    const updated = dto2Profile(result);
    updated.avatar += `?t=${Date.now()}`; // refresh bff cache
    mutate(updated);
  });

  const changePassword = createMutation(async (currentPassword: string, newPassword: string) => {
    const result = await meRepo.changePassword({ currentPassword, clearTextPassword: newPassword });
    mutate(dto2Profile(result));
  });

  const signOut = async () => {
    await redirectToAuth();
  };

  const [query] = useSearchParams<{ redirect?: string }>();

  const initAndStoreCompanies = async () => {
    let path = query.redirect ?? '/';
    try {
      const companies = await companyRepository.getCompanies();
      const localCompanyId = localStorage.getItem('companiesId');
      if (localCompanyId !== companies.id) {
        path = '/';
      }
      if (!companies.onboarded && path === '/' && !isDemo()) {
        path = '/onboarding';
      }
      localStorage.setItem('companiesId', companies.id);
    } finally {
      window.location.replace(path);
    }
  };

  return {
    get me() {
      fetch();
      return me;
    },
    refetch,
    updateProfile,
    updateAvatar,
    changePassword,
    signOut,
    initAndStoreCompanies,
    loginType,
    setLoginType,
  };
});
